import { useState, useRef, useCallback } from 'react'
import { brandApi } from '../api'
import { useEscapeKey } from '../useEscapeKey'
import { ErrorState, errorMessage } from './ErrorState'
import { useT } from '../i18n'
import type { BrandAuthImportRow, BrandAuthImportReport, BrandAuthStatus } from '../types'

/**
 * Import des autorisations marchand × marque depuis un CSV.
 *
 * Sans ces autorisations, le croisement du scan N4 est inerte : tout site
 * remonte comme inconnu, y compris les marchands déclarés. C'est la donnée
 * qui donne son sens au niveau 4.
 *
 * Le flux impose une simulation avant toute écriture : on montre exactement
 * ce qui sera créé, modifié et rejeté, puis on demande confirmation.
 */

const VALID_STATUSES: BrandAuthStatus[] = ['ACTIVE', 'SUSPENDED', 'PENDING', 'REVOKED']

interface Props {
  onClose: () => void
  /** Appelé après un import réellement appliqué, pour rafraîchir la liste. */
  onImported: () => void
}

export function BrandAuthImport({ onClose, onImported }: Props) {
  const t = useT()
  const [rows, setRows] = useState<BrandAuthImportRow[]>([])
  const [fileName, setFileName] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [preview, setPreview] = useState<BrandAuthImportReport | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<BrandAuthImportReport | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEscapeKey(true, onClose)

  const reset = () => {
    setRows([])
    setFileName('')
    setParseError(null)
    setPreview(null)
    setError(null)
    setDone(null)
  }

  const parseFile = useCallback((file: File) => {
    reset()
    setFileName(file.name)

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      if (!text) {
        setParseError(t('authImport.emptyFile'))
        return
      }

      const lines = text.split(/\r?\n/).filter((l) => l.trim())
      if (lines.length < 2) {
        setParseError(t('authImport.tooShort'))
        return
      }

      const sep = lines[0].includes(';') ? ';' : ','
      const headers = lines[0].split(sep).map((h) => h.trim().toLowerCase().replace(/['"]/g, ''))

      const merchantIdx = headers.findIndex((h) => ['merchant', 'marchand', 'url', 'domaine', 'domain'].includes(h))
      const brandIdx = headers.findIndex((h) => ['brand', 'marque'].includes(h))
      const statusIdx = headers.findIndex((h) => ['status', 'statut'].includes(h))
      const contractIdx = headers.findIndex((h) => ['contractref', 'contrat', 'contract', 'reference'].includes(h))

      if (merchantIdx === -1 || brandIdx === -1) {
        setParseError(t('authImport.missingCols'))
        return
      }

      const parsed: BrandAuthImportRow[] = []
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ''))
        const merchant = cols[merchantIdx] ?? ''
        const brand = cols[brandIdx] ?? ''
        if (!merchant && !brand) continue

        const rawStatus = statusIdx !== -1 ? (cols[statusIdx] ?? '').toUpperCase() : ''
        parsed.push({
          merchant,
          brand,
          status: VALID_STATUSES.includes(rawStatus as BrandAuthStatus)
            ? (rawStatus as BrandAuthStatus)
            : undefined,
          contractRef: contractIdx !== -1 ? cols[contractIdx] || undefined : undefined,
        })
      }

      if (parsed.length === 0) {
        setParseError(t('authImport.noUsableRow'))
        return
      }
      setRows(parsed)
    }
    reader.onerror = () => setParseError(t('authImport.readError'))
    reader.readAsText(file)
  }, [t])

  const runSimulation = async () => {
    setBusy(true)
    setError(null)
    try {
      setPreview(await brandApi.importAuthorizations(rows, false))
    } catch (err: unknown) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const applyImport = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await brandApi.importAuthorizations(rows, true)
      setDone(result)
      onImported()
    } catch (err: unknown) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const willWrite = preview ? preview.created + preview.updated : 0

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-header">
          <h3>{t('authImport.title')}</h3>
          <button type="button" className="modal-close" aria-label={t('common.close')} onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {done ? (
            <div className="import-done">
              <p className="import-done-title">{t('authImport.doneTitle')}</p>
              <p className="import-done-msg">
                {t('authImport.doneMsg', { created: done.created, updated: done.updated, unchanged: done.unchanged })}
                {done.errors.length > 0 && t('authImport.doneRejected', { n: done.errors.length })}
              </p>
              <button type="button" className="btn btn-secondary" onClick={reset}>
                {t('authImport.another')}
              </button>
            </div>
          ) : (
            <>
              <p className="import-help">{t('authImport.help')}</p>

              <div
                className="import-drop"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  const file = e.dataTransfer.files?.[0]
                  if (file) parseFile(file)
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) parseFile(file)
                  }}
                />
                <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
                  {t('authImport.chooseFile')}
                </button>
                <span className="import-drop-hint">
                  {fileName ? t('authImport.fileInfo', { file: fileName, n: rows.length }) : t('authImport.dropHint')}
                </span>
              </div>

              {parseError && <div className="alert-error">{parseError}</div>}
              {error && <ErrorState message={error} onRetry={preview ? applyImport : runSimulation} compact />}

              {preview && (
                <div className="import-preview">
                  <p className="import-preview-title">{t('authImport.previewTitle')}</p>
                  <ul className="import-preview-stats">
                    <li><strong>{preview.created}</strong> {t('authImport.toCreate')}</li>
                    <li><strong>{preview.updated}</strong> {t('authImport.toUpdate')}</li>
                    <li><strong>{preview.unchanged}</strong> {t('authImport.upToDate')}</li>
                    <li className={preview.errors.length ? 'import-stat-bad' : ''}>
                      <strong>{preview.errors.length}</strong> {t('authImport.rejected')}
                    </li>
                  </ul>

                  {preview.errors.length > 0 && (
                    <div className="import-errors">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th scope="col">{t('authImport.colRow')}</th>
                            <th scope="col">{t('authImport.colContent')}</th>
                            <th scope="col">{t('authImport.colReason')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.errors.slice(0, 50).map((e) => (
                            <tr key={e.row}>
                              <td>{e.row}</td>
                              <td>{e.label}</td>
                              <td className="import-error-msg">{e.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {preview.errors.length > 50 && (
                        <p className="import-help">{t('authImport.moreErrors', { n: preview.errors.length - 50 })}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {!done && (
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            {!preview ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={rows.length === 0 || busy}
                onClick={runSimulation}
              >
                {busy ? t('authImport.analysing') : t('authImport.simulate')}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || willWrite === 0}
                onClick={applyImport}
              >
                {busy ? t('common.importing') : t('authImport.apply', { n: willWrite })}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
